import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChangeEvent, OnSuggestionSelected, SuggestionSelectedEventData } from 'react-autosuggest';
import { debounce } from 'throttle-debounce';
import { useUiTheme } from 'framework/uiTheme';
import useInputHelper from './inputHelper';
import { ResultResourceData } from 'framework/crud';
import { DefaultInputLayoutProps, InputLayoutHintProps, InputLayoutStateProps, ValidationLayoutProps } from './types';
import type { SxProps, Theme } from '@mui/material';
import { LoadingParams } from 'framework/list';

const DEBOUNCE_THRESHOLD = 700;
const DEFAULT_NUMBER_RESULTS_PER_PAGE = 30;

export type Suggestion = {
  id: number | string;
  text: string;
  [key: string]: unknown;
};

interface AutosuggestDropdownProps {
  source: string;
  loadSuggestionsMethod: (searchValue: string, loadCurrentPage: number) => ResultResourceData;
  mapResults: (data: any) => Suggestion;
  noResultMessage?: string;
  resultsPerPage?: number;
  initialValue?: string | number;
  isLoading?: boolean;
  valueDecorator?: (searchValue: string) => string;
  onInputChangeCallback?: (resultValue: string) => void;
  buildAutoSuggestLabel?: (info: Suggestion) => string;
  searchRequest: (params: LoadingParams) => ResultResourceData;
  className?: string;
  placeholder?: string;
  InputIcon?: React.ReactNode;
  EndAdornment?: React.ReactNode;
  renderSuggestion?: (suggestion: Suggestion) => React.ReactNode;
  selectSuggestionMethod?: (searchValueId: StringOrNumber) => void;
  isInfinityLoading?: boolean;
  customGetInitialValue?: () => Promise<string>;
  label?: string;
  sx?: SxProps<Theme>;
}

export interface AutosuggestionDropdownLayoutProps
  extends DefaultInputLayoutProps, InputLayoutHintProps, InputLayoutStateProps, ValidationLayoutProps {
  suggestions: Suggestion[];
  suggestionsLoaded: boolean;
  suggestionsTotalPages: number;
  onSuggestionsFetchRequested: ({ value }: { value: string; reason: string; loadNextPage?: boolean }) => void;
  onSuggestionsClearRequested: () => void;
  onSuggestionSelected: OnSuggestionSelected<Suggestion>;
  onChangeCallback: (event: React.FormEvent<HTMLElement>, { newValue }: ChangeEvent) => void;
  onBlurCallback?: () => void;
  onFocusCallback?: () => void;
  noResultMessage: string;
  resultsPerPage: number;
  loading: boolean;
  InputIcon?: React.ReactNode;
  EndAdornment?: React.ReactNode;
  renderSuggestion?: (suggestion: Suggestion) => React.ReactNode;
  suggestionsPageRef: React.MutableRefObject<number>;
  isInfinityLoading?: boolean;
}

export type AutosuggestionDropdownLayoutType = ReactComponent<AutosuggestionDropdownLayoutProps>;

const toAutosuggestDisplayString = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return '';
};

const AutosuggestDropdown: React.FC<AutosuggestDropdownProps> = ({
  loadSuggestionsMethod,
  buildAutoSuggestLabel,
  noResultMessage = '',
  resultsPerPage = DEFAULT_NUMBER_RESULTS_PER_PAGE,
  initialValue = '',
  mapResults,
  isLoading = false,
  searchRequest,
  EndAdornment,
  onInputChangeCallback,
  renderSuggestion,
  valueDecorator,
  selectSuggestionMethod,
  customGetInitialValue,
  ...props
}) => {
  const { AutosuggestionDropdownLayout } = useUiTheme<AutosuggestionDropdownLayoutType>();
  const inputHelper = useInputHelper(props);
  const source = inputHelper.getSource();
  const formValue = inputHelper.getValue();
  const onChangeCallback = inputHelper.getRawOnChangeCallback();
  const safeInitialValue = toAutosuggestDisplayString(initialValue);

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const suggestionsPageRef = useRef(0);
  const [suggestionsTotalPages, setSuggestionsTotalPages] = useState(0);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
  const [loading, setLoading] = useState(isLoading);
  const [selectedValue, setSelectedValue] = useState(safeInitialValue);

  const fetchInitialValue = async () => {
    if (customGetInitialValue) {
      setLoading(true);
      try {
        const newValue = await customGetInitialValue();
        setSelectedValue(newValue);
      } catch (error) {
        throw new Error('Failed to fetch initial value');
      } finally {
        setLoading(false);
      }
    } else if (safeInitialValue && buildAutoSuggestLabel) {
      setLoading(true);
      try {
        const initialValueSearchParams: LoadingParams = {
          filter: { id: { eq: safeInitialValue } },
          pageNumber: 0,
          pageSize: resultsPerPage,
        };
        const { result = [] } = await searchRequest(initialValueSearchParams);
        const info = result.find(({ id }: Suggestion) => id === Number(safeInitialValue));

        if (info) {
          setSelectedValue(buildAutoSuggestLabel(info));
        }
      } catch (error) {
        throw new Error('Failed to fetch initial value');
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedValue !== formValue) {
      fetchInitialValue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValue]);

  const handleInputChange = onInputChangeCallback || (() => onChangeCallback?.({ [source]: null }));

  const loadSuggestions = useCallback(
    async (searchValue: string, loadNextPage?: boolean) => {
      setLoading(true);
      try {
        const resultSearchValue = valueDecorator ? valueDecorator(searchValue) : searchValue;
        const nextPage = loadNextPage ? suggestionsPageRef.current + 1 : 0;

        const { result, totalPages } = await loadSuggestionsMethod(resultSearchValue, nextPage);

        if (loadNextPage) {
          suggestionsPageRef.current += 1;
          setSuggestions(prev => [...prev, ...result.map(mapResults).filter(Boolean)]);
        } else {
          suggestionsPageRef.current = 0;
          setSuggestions(result.map(mapResults).filter(Boolean));
        }

        setSuggestionsTotalPages(totalPages);
        setSuggestionsLoaded(true);
      } catch (error) {
        throw new Error('Error loading suggestions');
      } finally {
        setLoading(false);
      }
    },
    [loadSuggestionsMethod, mapResults, valueDecorator],
  );

  const loadSuggestionsRef = useRef(loadSuggestions);

  useEffect(() => {
    loadSuggestionsRef.current = loadSuggestions;
  }, [loadSuggestions]);

  const debounceMethodRef = useRef<any>();

  useEffect(() => {
    debounceMethodRef.current = debounce(DEBOUNCE_THRESHOLD, (searchValue: string, loadNextPage?: boolean) => {
      loadSuggestionsRef.current(searchValue, loadNextPage);
    });

    return () => {
      if (debounceMethodRef.current && debounceMethodRef.current.cancel) {
        debounceMethodRef.current.cancel();
      }
    };
  }, []);

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
    setSuggestionsLoaded(false);
  };

  const onSuggestionsFetchRequested = ({
    value: searchValue,
    loadNextPage,
  }: {
    value: string;
    reason: string;
    loadNextPage?: boolean;
  }) => {
    debounceMethodRef.current(searchValue, loadNextPage);
  };

  const onChange = (event: React.FormEvent<HTMLElement>, { newValue }: ChangeEvent) => {
    const resultValue = valueDecorator ? valueDecorator(newValue) : newValue;
    setSelectedValue(resultValue);
    handleInputChange(resultValue);

    return resultValue;
  };

  const onSuggestionSelected = (
    event: React.FormEvent<any>,
    { suggestion }: SuggestionSelectedEventData<Suggestion>,
  ) => {
    const { text, id, ...otherFields } = suggestion;
    setSelectedValue(text);

    if (selectSuggestionMethod) {
      selectSuggestionMethod(id);
    }

    if (onChangeCallback) {
      onChangeCallback({
        [source]: id,
        ...otherFields,
      });
    }
  };

  return (
    <AutosuggestionDropdownLayout
      {...props}
      suggestionsPageRef={suggestionsPageRef}
      suggestions={suggestions}
      suggestionsLoaded={suggestionsLoaded}
      suggestionsTotalPages={suggestionsTotalPages}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      onSuggestionSelected={onSuggestionSelected}
      source={source}
      value={selectedValue}
      onChangeCallback={onChange}
      loading={loading}
      EndAdornment={EndAdornment}
      renderSuggestion={renderSuggestion}
      isRequired={inputHelper.getIsRequired()}
      isVisible={inputHelper.isVisible()}
      errorMessages={inputHelper.getErrorMessages()}
      onFocusCallback={inputHelper.getClearValidationCallback()}
      onBlurCallback={inputHelper.getValidationCallback()}
      noResultMessage={noResultMessage}
      resultsPerPage={resultsPerPage}
      labelHint={inputHelper.getLabelHint()}
      disabled={inputHelper.getIsDisabled()}
    />
  );
};

export default AutosuggestDropdown;
